// The session store: a hand-rolled, dependency-free external store (read via
// useSyncExternalStore, see useSession.ts). It holds the whole live session and
// is the ONE place segment.speakerId mutates — late provider relabels and user
// merge/rename both flow through here so every subscriber updates atomically
// with no transcript text loss (001 §7 / AC4 / 002 AC2).
//
// Identity contract: each mutation produces a NEW snapshot object (so the root
// changes and selectors re-run) while REUSING the references of fields that did
// not change (so a level tick doesn't re-render the transcript).

import type { AiPresence, AiStatus, AiTurn } from '@/lib/ai/types'
import type {
  ConnState,
  ProviderError,
  Speaker,
  SpeakerId,
  TranscriptSegment
} from '@/lib/transcription/types'

// Semantic colour utilities (tokens.css), assigned round-robin as speakers
// appear. Kept stable across reload + merge by storing the colour on the
// Speaker record rather than deriving it from position.
const SPEAKER_COLORS = [
  'text-accent',
  'text-success',
  'text-warn',
  'text-danger',
  'text-text',
  'text-muted-foreground'
] as const

export interface SessionSnapshot {
  id: string
  title: string
  createdAt: number
  finals: TranscriptSegment[]
  interim: TranscriptSegment[]
  speakers: Record<SpeakerId, Speaker>
  connection: ConnState
  connectionDetail: string
  level: number
  error: ProviderError | null
  // --- AI participant (PRD 003). aiTurns is mirrored to IDB so a reload
  // restores the AI feed (sessionPersistence.ts); aiStatus/aiDetail stay
  // volatile (they describe the live round, not the durable transcript). ---
  aiStatus: AiStatus
  aiDetail: string
  aiTurns: AiTurn[]
  // Liveness of the two AI layers (reflex + the user's Claude Code), derived
  // from /bridge/health by the bridge client and rendered as two status chips.
  // Volatile — describes the live round, like aiStatus.
  aiPresence: AiPresence
  // Per-transcript-segment delivery to the bridge (a WhatsApp-style read
  // receipt). `sent` = the gateway acked the POST; `heard` = the agent actually
  // drained it via wait_for_transcript. Absent = not forwarded yet. Volatile;
  // the bridge client is the only writer (lets the room eyeball round-trip
  // latency). See bridgeClient.ts.
  delivery: Record<string, SegmentDelivery>
}

export type SegmentDelivery = 'sent' | 'heard'

export interface SessionStore {
  getSnapshot(): SessionSnapshot
  subscribe(listener: () => void): () => void

  // --- pipeline-driven ---
  applyResult(
    finals: TranscriptSegment[],
    interim: TranscriptSegment[],
    finalsChanged: boolean
  ): void
  /** Create the speaker (with a colour) on first sight of its provider label. */
  ensureSpeaker(id: SpeakerId, providerLabel: string): void
  setConnection(state: ConnState, detail?: string): void
  setLevel(level: number): void
  setError(error: ProviderError | null): void

  // --- user-driven ---
  /** Rename the session (sidebar/header). Persisted by attachPersistence. */
  setTitle(title: string): void
  renameSpeaker(id: SpeakerId, displayName: string): void
  /** Fold `remove` into `keep`: rewrite every segment, drop the speaker. */
  mergeSpeakers(keep: SpeakerId, remove: SpeakerId): void
  relabelSegment(segmentId: string, speakerId: SpeakerId): void
  /** Bind (or unbind, with null) a speaker to a contact-book entry. */
  linkSpeakerContact(id: SpeakerId, contactId: string | null): void

  // --- AI participant (bridgeClient.ts is the only writer) ---
  setAiStatus(status: AiStatus, detail?: string): void
  /** Update the reflex/agent liveness chips. No-op when value-equal (the health
   *  poll runs sub-second, so most ticks don't change presence). */
  setAiPresence(next: AiPresence): void
  /** Add an inline contribution (a typed card / a present bubble). */
  addAiTurn(turn: AiTurn): void
  /** Append text to a pending turn (fills a loading card opened earlier). */
  appendAiTurn(id: string, chunk: string): void
  /** Patch a still-pending delegation card in place (no new card): its `queued`
   *  state (waiting in line ↔ the agent is working it) and/or its loading label
   *  (a superseded delegation refines its brief). See Signal.queued. */
  updateAiTurnPending(id: string, patch: { queued?: boolean; label?: string }): void
  /** Clear the pending/streaming flag — the contribution is complete. `opts.speak`
   *  sets the read-aloud flag from the completing signal. */
  finishAiTurn(id: string, opts?: { speak?: boolean }): void
  /** Toggle the user's "handled" tick on a contribution (greys it / dims its
   *  scrubber chip). */
  toggleAiTurnDone(id: string): void
  /** Replace the per-segment bridge delivery map (read receipts). No-op when the
   *  next map is value-equal to the current one (avoids re-render churn). */
  setSegmentDelivery(next: Record<string, SegmentDelivery>): void
}

export interface SessionInit {
  id: string
  title: string
  createdAt?: number
  finals?: TranscriptSegment[]
  speakers?: Record<SpeakerId, Speaker>
  /** Restored AI contributions (a reloaded session). Any card still flagged
   *  streaming is coerced to finished on seed — the live round that would have
   *  filled it is gone, so it must not rehydrate as a stuck loading spinner. */
  aiTurns?: AiTurn[]
}

// Turn identity is load-bearing: `id` is the React key in the feed, the
// done-toggle target, and the pending-card fill correlation. A collision makes
// checking one card flip another and the feed render the wrong node. The agent
// can reuse an id across signals (a reflex re-send / a recycled delegation id),
// so the store — the only writer — guarantees uniqueness on every add and on
// seed, keeping both contributions but giving the later one a fresh id.
function uniquify(id: string, taken: Set<string>): string {
  if (!taken.has(id)) return id
  let n = 2
  while (taken.has(`${id}#${n}`)) n++
  return `${id}#${n}`
}

export function createSessionStore(init: SessionInit): SessionStore {
  // Seed restored turns through the same uniqueness guard so a session saved
  // with duplicate ids (a pre-fix record) reopens healed rather than cross-
  // toggling. Any card still flagged streaming is coerced finished (the live
  // round that would fill it is gone — see SessionInit.aiTurns).
  const seedIds = new Set<string>()
  const seededTurns = (init.aiTurns ?? []).map((t) => {
    const id = uniquify(t.id, seedIds)
    seedIds.add(id)
    const next = id !== t.id || t.streaming ? { ...t, id, streaming: false } : t
    return next
  })

  let state: SessionSnapshot = {
    id: init.id,
    title: init.title,
    createdAt: init.createdAt ?? Date.now(),
    finals: init.finals ?? [],
    interim: [],
    speakers: init.speakers ?? {},
    connection: 'idle',
    connectionDetail: '',
    level: 0,
    error: null,
    aiStatus: 'idle',
    aiDetail: '',
    aiTurns: seededTurns,
    aiPresence: { reflex: 'connecting', agent: 'off' },
    delivery: {}
  }
  // Colour cursor advances per speaker created; survives the session.
  let colorCursor = Object.keys(state.speakers).length

  const listeners = new Set<() => void>()

  function set(patch: Partial<SessionSnapshot>): void {
    state = { ...state, ...patch }
    for (const l of listeners) l()
  }

  function rewriteSpeaker(
    segments: TranscriptSegment[],
    from: SpeakerId,
    to: SpeakerId
  ): TranscriptSegment[] {
    let changed = false
    const next = segments.map((s) => {
      if (s.speakerId !== from) return s
      changed = true
      return { ...s, speakerId: to }
    })
    return changed ? next : segments
  }

  return {
    getSnapshot: () => state,
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },

    applyResult(finals, interim, finalsChanged) {
      const patch: Partial<SessionSnapshot> = {}
      // Snapshot the segmenter's internal finals array into a stable new ref
      // only when it actually changed.
      if (finalsChanged) patch.finals = finals.slice()
      // Keep the empty-array reference stable so final-only updates don't
      // re-render the (empty) interim tail.
      if (!(interim.length === 0 && state.interim.length === 0)) {
        patch.interim = interim
      }
      if (Object.keys(patch).length) set(patch)
    },

    ensureSpeaker(id, providerLabel) {
      if (state.speakers[id]) return
      const color =
        SPEAKER_COLORS[colorCursor % SPEAKER_COLORS.length] ?? 'text-text'
      colorCursor++
      set({
        speakers: {
          ...state.speakers,
          [id]: { id, providerLabel, displayName: null, color }
        }
      })
    },

    setConnection(connection, detail = '') {
      if (state.connection === connection && state.connectionDetail === detail) {
        return
      }
      set({ connection, connectionDetail: detail })
    },

    setLevel(level) {
      if (state.level === level) return
      set({ level })
    },

    setError(error) {
      set({ error })
    },

    setTitle(title) {
      const next = title.trim()
      if (!next || next === state.title) return
      set({ title: next })
    },

    renameSpeaker(id, displayName) {
      const sp = state.speakers[id]
      if (!sp) return
      const name = displayName.trim() || null
      set({ speakers: { ...state.speakers, [id]: { ...sp, displayName: name } } })
    },

    mergeSpeakers(keep, remove) {
      if (keep === remove || !state.speakers[remove]) return
      const speakers = { ...state.speakers }
      delete speakers[remove]
      set({
        finals: rewriteSpeaker(state.finals, remove, keep),
        interim: rewriteSpeaker(state.interim, remove, keep),
        speakers
      })
    },

    linkSpeakerContact(id, contactId) {
      const sp = state.speakers[id]
      if (!sp || sp.contactId === contactId) return
      set({ speakers: { ...state.speakers, [id]: { ...sp, contactId } } })
    },

    relabelSegment(segmentId, speakerId) {
      const apply = (segs: TranscriptSegment[]) => {
        let changed = false
        const next = segs.map((s) => {
          if (s.id !== segmentId) return s
          changed = true
          return { ...s, speakerId }
        })
        return changed ? next : segs
      }
      const finals = apply(state.finals)
      const interim = apply(state.interim)
      if (finals !== state.finals || interim !== state.interim) {
        set({ finals, interim })
      }
    },

    setAiStatus(aiStatus, detail = '') {
      if (state.aiStatus === aiStatus && state.aiDetail === detail) return
      set({ aiStatus, aiDetail: detail })
    },

    setAiPresence(next) {
      const cur = state.aiPresence
      if (cur.reflex === next.reflex && cur.agent === next.agent) return
      set({ aiPresence: next })
    },

    addAiTurn(turn) {
      // Guarantee a unique id (see uniquify): a pending card is added before its
      // fill arrives so it never collides; a terminal card whose id was already
      // used (a re-sent / recycled signal id) keeps its content but gets a fresh
      // id so it can't hijack the other card's toggle or React key.
      const taken = new Set(state.aiTurns.map((t) => t.id))
      const id = uniquify(turn.id, taken)
      const next = id === turn.id ? turn : { ...turn, id }
      set({ aiTurns: [...state.aiTurns, next] })
    },

    appendAiTurn(id, chunk) {
      let changed = false
      const aiTurns = state.aiTurns.map((t) => {
        if (t.id !== id) return t
        changed = true
        return { ...t, text: t.text + chunk }
      })
      if (changed) set({ aiTurns })
    },

    updateAiTurnPending(id, patch) {
      let changed = false
      const aiTurns = state.aiTurns.map((t) => {
        if (t.id !== id || !t.streaming) return t
        const nextQueued = patch.queued ?? t.queued
        const nextLabel = patch.label ?? t.label
        if (nextQueued === t.queued && nextLabel === t.label) return t
        changed = true
        return { ...t, queued: nextQueued, label: nextLabel }
      })
      if (changed) set({ aiTurns })
    },

    finishAiTurn(id, opts) {
      let changed = false
      const aiTurns = state.aiTurns.map((t) => {
        if (t.id !== id || !t.streaming) return t
        changed = true
        // The completing signal can carry its own speak flag (the pending card
        // was opened with the reflex's loading label, which never spoke).
        return {
          ...t,
          streaming: false,
          ...(opts?.speak !== undefined ? { speak: opts.speak } : {})
        }
      })
      if (changed) set({ aiTurns })
    },

    toggleAiTurnDone(id) {
      let changed = false
      const aiTurns = state.aiTurns.map((t) => {
        if (t.id !== id) return t
        changed = true
        return { ...t, completed: !t.completed }
      })
      if (changed) set({ aiTurns })
    },

    setSegmentDelivery(next) {
      const cur = state.delivery
      const nextKeys = Object.keys(next)
      let same = nextKeys.length === Object.keys(cur).length
      if (same) {
        for (const k of nextKeys) {
          if (cur[k] !== next[k]) {
            same = false
            break
          }
        }
      }
      if (!same) set({ delivery: next })
    }
  }
}
